import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Chrome, Mail, ArrowLeft, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import databullLogo from "@/assets/databulllogo.png";
import { APP_NAME, APP_NAME_FULL } from "@/lib/brand";

// ── Current client urlslug (null on app/root domains) ─────────────────────
function getCurrentUrlslug(): string | null {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") return null;
  const parts = hostname.split(".");
  if (parts.length < 3) return null; // databull.ai — no subdomain
  const sub = parts[0];
  return sub === "app" ? null : sub;
}

// ── Browser fingerprint ────────────────────────────────────────────────────
function getBrowserId(): string {
  const KEY = "dfg_browser_id";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

// ── Rate-limit state (mirrors server-side, for optimistic UX) ─────────────
const RL_KEY = "dfg_rl";
const RL_MAX = 3;
const RL_WINDOW_MS = 5 * 60 * 1000;

interface RLState {
  attempts: number[];  // timestamps of attempts in current window
}

function getRLState(): RLState {
  try {
    const raw = localStorage.getItem(RL_KEY);
    if (raw) return JSON.parse(raw) as RLState;
  } catch {
    // ignore
  }
  return { attempts: [] };
}

function recordAttempt() {
  const now = Date.now();
  const state = getRLState();
  const fresh = state.attempts.filter((t) => now - t < RL_WINDOW_MS);
  fresh.push(now);
  localStorage.setItem(RL_KEY, JSON.stringify({ attempts: fresh }));
}

function getRemainingAttempts(): number {
  const now = Date.now();
  const state = getRLState();
  const fresh = state.attempts.filter((t) => now - t < RL_WINDOW_MS);
  return Math.max(0, RL_MAX - fresh.length);
}

function getMsUntilReset(): number {
  const now = Date.now();
  const state = getRLState();
  const fresh = state.attempts.filter((t) => now - t < RL_WINDOW_MS);
  if (fresh.length < RL_MAX) return 0;
  const oldest = Math.min(...fresh);
  return RL_WINDOW_MS - (now - oldest);
}

// ── Component ──────────────────────────────────────────────────────────────
type Step = "initial" | "email-entry" | "code-entry";

const Auth = () => {
  const [step, setStep] = useState<Step>("initial");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0); // seconds until rate-limit resets
  const { toast } = useToast();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const location = useLocation();

  // Show access-denied error passed via navigation state (e.g. wrong-client login)
  useEffect(() => {
    const err = (location.state as { error?: string } | null)?.error;
    if (err) {
      toast({ title: "Access denied", description: err, variant: "destructive" });
    }
  }, []);

  // Keep countdown updated
  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current!);
  }, [countdown]);

  const handleGoogleLogin = async () => {
    // Use the current origin as the OAuth callback so the URL stays on the
    // client's subdomain after Google login (no bounce through app.databull.ai).
    // Each urlslug domain must be whitelisted in Supabase → Auth → Redirect URLs.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth` },
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  /** Step 1 → 2: send login code */
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side rate limit check
    const remaining = getRemainingAttempts();
    if (remaining <= 0) {
      const ms = getMsUntilReset();
      setCountdown(Math.ceil(ms / 1000));
      toast({
        title: "Too many attempts",
        description: "Please wait before requesting another code.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      recordAttempt();

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-login-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            email: email.trim(),
            browser_id: getBrowserId(),
            urlslug: getCurrentUrlslug(),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403 && data.wrong_client) {
          toast({
            title: "Access denied",
            description: data.error ?? "You don't have access to this workspace.",
            variant: "destructive",
          });
          return;
        }
        if (res.status === 403 && data.ip_blocked) {
          toast({
            title: "Access Denied",
            description: data.error ?? "Your IP address is not whitelisted.",
            variant: "destructive",
          });
          return;
        }
        if (res.status === 429) {
          // Server-side rate limit — sync client state
          const ms = getMsUntilReset();
          setCountdown(Math.max(Math.ceil(ms / 1000), 60));
          toast({
            title: "Too many attempts",
            description: data.error ?? "Please wait 5 minutes before trying again.",
            variant: "destructive",
          });
          return;
        }
        throw new Error(data.error ?? "Failed to send login code");
      }

      setStep("code-entry");
      toast({ title: "Code sent!", description: "Check your email for the 8-character login code." });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  /** Step 2 → signed in: verify code and exchange for session */
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-login-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            email: email.trim(),
            code: code.trim(),
            urlslug: getCurrentUrlslug(),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403 && data.wrong_client) {
          toast({
            title: "Access denied",
            description: data.error ?? "You don't have access to this workspace.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        if (res.status === 403 && data.ip_blocked) {
          toast({
            title: "Access Denied",
            description: data.error ?? "Your IP address is not whitelisted.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        throw new Error(data.error ?? "Verification failed");
      }

      // Exchange the hashed magic-link token for a real session
      const { error: authError } = await supabase.auth.verifyOtp({
        token_hash: data.token_hash,
        type: "magiclink",
      });

      if (authError) {
        throw new Error(authError.message);
      }

      // AuthProvider will detect the session change and redirect automatically
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(step === "code-entry" ? "email-entry" : "initial");
    setCode("");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img
            src={databullLogo}
            alt={APP_NAME_FULL}
            className="mx-auto mb-2 h-20 w-20 sm:h-32 sm:w-32 rounded-2xl object-cover"
          />
          <CardTitle className="text-xl sm:text-2xl">{APP_NAME_FULL}</CardTitle>
          <CardDescription className="text-sm">Unlock growth with unified data intelligence</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* ── Step: Initial (choose method) ── */}
          {step === "initial" && (
            <>
              <Button onClick={handleGoogleLogin} variant="outline" className="w-full gap-2">
                <Chrome className="h-4 w-4" />
                Continue with Google
              </Button>

              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">or</span>
                <Separator className="flex-1" />
              </div>

              <Button
                onClick={() => setStep("email-entry")}
                variant="outline"
                className="w-full gap-2"
              >
                <Mail className="h-4 w-4" />
                Continue with Email
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                By continuing, you agree to our{" "}
                <a href="https://databull.ai/terms" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a target="_blank" href="https://databull.ai/privacy" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  Privacy Policy
                </a>
                .
              </p>
            </>
          )}

          {/* ── Step: Email entry ── */}
          {step === "email-entry" && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {countdown > 0 && (
                <p className="text-sm text-destructive text-center">
                  Too many attempts. Try again in{" "}
                  <span className="font-semibold tabular-nums">
                    {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
                  </span>
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || countdown > 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending code…
                  </>
                ) : (
                  "Send login code"
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                We'll send an 8-character code to your inbox. Up to {RL_MAX} requests per 5 minutes.
              </p>
            </form>
          )}

          {/* ── Step: Code entry ── */}
          {step === "code-entry" && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>

              <div className="text-center space-y-1 py-2">
                <Mail className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">Check your inbox</p>
                <p className="text-xs text-muted-foreground">
                  We sent a login code to <span className="font-medium">{email}</span>
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="code">Login code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="XXXXXXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  autoFocus
                  className="text-center text-xl font-mono tracking-widest uppercase"
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading || code.length < 8}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Didn't receive the code?{" "}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-foreground transition-colors disabled:opacity-50"
                  onClick={() => setStep("email-entry")}
                  disabled={countdown > 0}
                >
                  Resend
                </button>
                {countdown > 0 && (
                  <span>
                    {" "}
                    (
                    {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
                    )
                  </span>
                )}
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
